import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { Table, Tbody, Th, Thead, Tr } from '@patternfly/react-table';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonationTargets } from '../../../hooks/useImpersonationTargets';
import { useProductPricing } from '../../../hooks/useProductPricing';
import { DECIMAL_RE, formatProductPrice, formatRecurring } from '../../../utils/money';
import type { AdminProduct } from '../../../types/models/AdminProduct';
import type {
  ApiResponseAdminProductCleared,
  ApiResponseAdminProductOverride,
} from '../../../types/api/ApiResponseAdminProduct';
import ClientTypeahead from './ClientTypeahead';
import ClientPriceOverrideRow, { type OverrideMutationResult } from './ClientPriceOverrideRow';

interface ClientPricesModalProps {
  product: AdminProduct;
  isOpen: boolean;
  onClose: () => void;
  /** Report the product's new override count to the grid so its row badge stays in sync. */
  onOverrideCountChange: (productId: number, count: number) => void;
}

// Manage a product's per-client price overrides. Mounted per-product (keyed) only while open, so its
// pricing fetch and form state are always fresh. Add/edit/remove each hit the backend, branch on the
// JSend body.status, notify + open the drawer on both outcomes, then refetch the list in place.
function ClientPricesModal({ product, isOpen, onClose, onOverrideCountChange }: ClientPricesModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { pricing, error, refetch } = useProductPricing(product.id, isOpen);
  const { targets, loading: targetsLoading, error: targetsError } = useImpersonationTargets();

  const [addClientId, setAddClientId] = useState<number | null>(null);
  const [addDraft, setAddDraft] = useState('');
  const [addServerError, setAddServerError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const overrides = pricing?.overrides ?? [];

  const addTrimmed = addDraft.trim();
  const isAddPriceValid = DECIMAL_RE.test(addTrimmed);
  const addHelperError =
    addServerError ??
    (addTrimmed !== '' && !isAddPriceValid
      ? 'Enter a non-negative amount with up to 4 decimal places.'
      : null);

  // The picker offers only clients that don't already have an override. Depends on the stable
  // `pricing` reference (not the `?? []` `overrides` expression, which is a fresh array each render).
  const availableClients = useMemo(() => {
    const taken = new Set((pricing?.overrides ?? []).map((o) => o.client_id));
    return targets.filter((t) => !taken.has(t.id)).map((t) => ({ id: t.id, name: t.name }));
  }, [targets, pricing]);

  // After a successful mutation, re-read the pricing and report the new override count to the grid.
  const refreshAndReport = async () => {
    const fresh = await refetch();
    if (fresh) {
      onOverrideCountChange(product.id, fresh.overrides.length);
    }
  };

  // PUT /admin/products/{id}/price/{clientId} — used for both add and edit.
  const putOverride = async (clientId: number, unitPrice: string): Promise<OverrideMutationResult> => {
    try {
      const response = await apiFetch(`/admin/products/${product.id}/price/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify({ unit_price: unitPrice }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminProductOverride | null;
      if (body?.status === 'success' && body.data?.price) {
        const clientName = body.data.price.client_name ?? `client #${clientId}`;
        addNotification({
          title: 'Client price saved',
          srTitle: 'Success alert',
          description: `${product.label} for ${clientName} is now ${formatProductPrice(body.data.price.unit_price, product.recurring)} (excl. VAT).`,
          variant: 'success',
        });
        setDrawerOpen(true);
        await refreshAndReport();
        return { ok: true, fieldError: null };
      }
      // Validation failure lands in data.unit_price; a domain-operation rejection in data.product.
      const fieldError = body?.data?.unit_price ?? body?.data?.product ?? null;
      addNotification({
        title: 'Could not save client price',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not save the client price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError };
    } catch (err) {
      addNotification({
        title: 'Could not save client price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  // DELETE /admin/products/{id}/price/{clientId}
  const deleteOverride = async (clientId: number): Promise<OverrideMutationResult> => {
    try {
      const response = await apiFetch(`/admin/products/${product.id}/price/${clientId}`, {
        method: 'DELETE',
      });
      const body = (await response.json().catch(() => null)) as ApiResponseAdminProductCleared | null;
      if (body?.status === 'success' && body.data?.cleared) {
        addNotification({
          title: 'Client price removed',
          srTitle: 'Success alert',
          description: `The client-specific price for ${product.label} was removed; that client reverts to the default.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        await refreshAndReport();
        return { ok: true, fieldError: null };
      }
      addNotification({
        title: 'Could not remove client price',
        srTitle: 'Error alert',
        description: body?.message ?? 'Could not remove the client price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    } catch (err) {
      addNotification({
        title: 'Could not remove client price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  const handleAdd = async () => {
    if (addClientId === null || !isAddPriceValid) {
      return;
    }
    setIsAdding(true);
    setAddServerError(null);
    const result = await putOverride(addClientId, addTrimmed);
    setIsAdding(false);
    if (result.ok) {
      setAddClientId(null);
      setAddDraft('');
    } else {
      setAddServerError(result.fieldError);
    }
  };

  const defaultPriceText =
    pricing?.default_unit_price != null
      ? formatProductPrice(pricing.default_unit_price, pricing.recurring)
      : 'Not set';

  const renderBody = () => {
    // Show the error only when we have nothing to display; once pricing is loaded a transient
    // refetch error keeps the (stale) table rather than blanking it.
    if (error && !pricing) {
      return (
        <Alert variant="danger" isInline title="Could not load client prices">
          {error}
        </Alert>
      );
    }

    if (!pricing) {
      return (
        <Flex justifyContent={{ default: 'justifyContentCenter' }} style={{ padding: '2rem' }}>
          <FlexItem>
            <Spinner aria-label="Loading client prices" />
          </FlexItem>
        </Flex>
      );
    }

    return (
      <>
        <DescriptionList isHorizontal isCompact className="pf-v6-u-mb-md">
          <DescriptionListGroup>
            <DescriptionListTerm>Platform default</DescriptionListTerm>
            <DescriptionListDescription>{defaultPriceText} (excl. VAT)</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Billing</DescriptionListTerm>
            <DescriptionListDescription>{formatRecurring(pricing.recurring)}</DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>

        {overrides.length > 0 ? (
          <Table aria-label={`Client price overrides for ${product.label}`} variant="compact">
            <Thead>
              <Tr>
                <Th>Client</Th>
                <Th>Price (excl. VAT)</Th>
                <Th screenReaderText="Row actions" />
              </Tr>
            </Thead>
            <Tbody>
              {overrides.map((o) => (
                <ClientPriceOverrideRow
                  key={o.client_id}
                  override={o}
                  recurring={product.recurring}
                  onSave={putOverride}
                  onRemove={deleteOverride}
                />
              ))}
            </Tbody>
          </Table>
        ) : (
          <Content component="p">
            No client-specific prices yet. Every client is billed the platform default; add an override below.
          </Content>
        )}

        <div className="pf-v6-u-mt-lg">
          <Content component="h3" className="pf-v6-u-mb-sm">
            Add a client price
          </Content>
          {targetsError && (
            <Alert variant="warning" isInline title="Could not load the client list" className="pf-v6-u-mb-sm">
              The list of clients could not be loaded, so a new override can&apos;t be added right now. Existing
              overrides above can still be edited or removed.
            </Alert>
          )}
          <Flex alignItems={{ default: 'alignItemsFlexStart' }} spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem grow={{ default: 'grow' }}>
              <ClientTypeahead
                clients={availableClients}
                value={addClientId}
                onChange={setAddClientId}
                isDisabled={isAdding || targetsLoading}
              />
            </FlexItem>
            <FlexItem>
              <TextInput
                id="admin-override-add-price"
                type="text"
                inputMode="decimal"
                value={addDraft}
                onChange={(_event, value) => {
                  setAddDraft(value);
                  if (addServerError) {
                    setAddServerError(null);
                  }
                }}
                validated={addHelperError ? 'error' : 'default'}
                isDisabled={isAdding}
                aria-label="New client unit price in euros, excluding VAT"
                placeholder="0.0000"
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                onClick={() => void handleAdd()}
                isLoading={isAdding}
                isDisabled={isAdding || addClientId === null || !isAddPriceValid}
              >
                Add
              </Button>
            </FlexItem>
          </Flex>
          {addHelperError && (
            <HelperText>
              <HelperTextItem variant="error">{addHelperError}</HelperTextItem>
            </HelperText>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={onClose} aria-label={`Client prices for ${product.label}`}>
      <ModalHeader title={`Client prices — ${product.label}`} description={product.product_desc || undefined} />
      <ModalBody tabIndex={0} aria-label={`Client prices for ${product.label}`}>{renderBody()}</ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ClientPricesModal;
